import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Briefcase, FileCheck, ShoppingCart, TrendingDown, Truck, UserCircle, Users } from 'lucide-react';
import { accountingService } from '../services/accountingService';
import { bidService } from '../services/bidService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';

type DashboardStats = {
  drivers: number;
  vehicles: number;
  openBidRequests: number;
  myBids: number;
  activeShipments: number;
  invoiceWaiting: number;
};

const initialStats: DashboardStats = {
  drivers: 0,
  vehicles: 0,
  openBidRequests: 0,
  myBids: 0,
  activeShipments: 0,
  invoiceWaiting: 0,
};

const cards: Array<{ id: string; key?: keyof DashboardStats; title: string; description: string; icon: any; href: string; color: string; value?: string }> = [
  { id: 'openBidRequests', key: 'openBidRequests', title: 'Teklif Talepleri', description: 'Açık talepleri inceleyin ve firmanız adına teklif verin.', icon: ShoppingCart, href: '/bid-marketplace', color: 'bg-blue-50 text-blue-700 border-blue-100' },
  { id: 'myBids', key: 'myBids', title: 'Verilen Teklifler', description: 'Firmanızın verdiği tekliflerin durumunu takip edin.', icon: TrendingDown, href: '/my-bids', color: 'bg-violet-50 text-violet-700 border-violet-100' },
  { id: 'activeShipments', key: 'activeShipments', title: 'Sevkiyatlar', description: 'Firmanıza bağlı devam eden işleri görüntüleyin.', icon: Briefcase, href: '/active-jobs', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { id: 'drivers', key: 'drivers', title: 'Sürücüler', description: 'Firmanıza bağlı mobil sürücüleri yönetin.', icon: Users, href: '/corporate-drivers', color: 'bg-orange-50 text-orange-700 border-orange-100' },
  { id: 'vehicles', key: 'vehicles', title: 'Çekiciler', description: 'Firmanıza bağlı çekicileri ve dorse plakalarını yönetin.', icon: Truck, href: '/vehicle-management?ownerType=external', color: 'bg-slate-50 text-slate-700 border-slate-100' },
  { id: 'invoiceWaiting', key: 'invoiceWaiting', title: 'Fatura Bekleyenler', description: 'Fatura bekleyen sevkiyatlara belge yükleyin.', icon: FileCheck, href: '/driver-mobile-earnings', color: 'bg-amber-50 text-amber-700 border-amber-100' },
  { id: 'profile', value: 'Hesap', title: 'Profil', description: 'Kurumsal hesap ve iletişim bilgilerinizi yönetin.', icon: UserCircle, href: '/corporate-profile', color: 'bg-rose-50 text-rose-700 border-rose-100' },
];

export function CorporateCarrierDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>(initialStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isSubUser = hasUserRole(user, 'corporate-external-transport-user') || (hasUserRole(user, 'corporate-external-transport') && Boolean((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id));
    if (isSubUser) navigate('/driver-mobile-home', { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [driversRes, vehiclesRes, bidRequestsRes, bidsRes, accountingRes] = await Promise.all([
          driverService.getApplications({ status: 'approved', all: true }).catch(() => ({ data: [] })),
          vehicleService.getVehicles({ ownerType: 'external' }).catch(() => ({ data: [] })),
          bidService.getBidRequests({ status: 'open' }).catch(() => ({ data: [] })),
          bidService.getAll().catch(() => ({ data: [] })),
          accountingService.getOverview().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const drivers = Array.isArray((driversRes as any)?.data) ? (driversRes as any).data : [];
        const vehicles = Array.isArray((vehiclesRes as any)?.data) ? (vehiclesRes as any).data : [];
        const bidRequests = Array.isArray((bidRequestsRes as any)?.data) ? (bidRequestsRes as any).data : [];
        const bids = Array.isArray((bidsRes as any)?.data) ? (bidsRes as any).data : [];
        const accountingRows = Array.isArray((accountingRes as any)?.data) ? (accountingRes as any).data : [];
        setStats({
          drivers: drivers.length,
          vehicles: vehicles.length,
          openBidRequests: bidRequests.length,
          myBids: bids.length,
          activeShipments: accountingRows.filter((row: any) => !['payment-completed', 'cancelled'].includes(String(row.accountingStatus || row.accounting_status || ''))).length,
          invoiceWaiting: accountingRows.filter((row: any) => ['invoice-waiting', 'invoice-rejected'].includes(String(row.accountingStatus || row.accounting_status || ''))).length,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-[calc(100vh-7rem)] bg-slate-50 p-3 pb-24 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-3xl bg-slate-950 p-6 text-white shadow-xl sm:p-8">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-300">Kurumsal Nakliye Paneli</div>
            <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Firmanızın sürücü, çekici, teklif ve fatura süreçleri</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300 sm:text-base">Bu panel yalnızca firmanıza ait operasyonları gösterir. Admin yönetim ekranları yerine kurumsal nakliye kullanımına göre sadeleştirilmiş akışları kullanırsınız.</p>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
          {cards.map((card) => {
            const Icon = card.icon;
              const value = card.key ? stats[card.key] : card.value;
              return (
              <Link key={card.id} to={card.href} className="group rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg sm:rounded-3xl sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className={`rounded-2xl border p-2 sm:p-3 ${card.color}`}><Icon className="h-5 w-5 sm:h-6 sm:w-6" /></div>
                  <div className="text-right text-2xl font-black text-slate-900 sm:text-3xl">{loading ? '...' : value}</div>
                </div>
                <div className="mt-4 text-sm font-bold text-slate-900 sm:mt-5 sm:text-lg">{card.title}</div>
                <p className="mt-2 hidden text-sm leading-6 text-slate-500 sm:block">{card.description}</p>
                <div className="mt-3 text-xs font-semibold text-blue-700 group-hover:text-blue-800 sm:mt-4 sm:text-sm">Ekrana git</div>
              </Link>
            );
          })}
        </section>
      </div>
    </div>
  );
}
